import React from 'react';
import NewTrailCard from '@alltrails/shared/components/TrailCard';
import BookmarkButton from '@alltrails/shared/components/BookmarkButton';
import { getTrailProfilePhotoUrl, getFormattedTrailUrls, getLocalizedName } from 'utils/trail_helpers';
import { wrapEventHandler } from 'utils/handlers';
import CustomProvider from 'components/CustomProvider';
import { Context } from '@alltrails/shared/types/context';
import { Trail } from 'types/Trails';
import useAdminFooterCTAs, { ButtonConfig } from 'hooks/useAdminFooterCTAs';
import useFeatures from 'hooks/useFeatures';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import useActivityDuration from 'hooks/useActivityDuration';
import { getTrailForTrailCard } from 'utils/cardInfo';
import * as styles from '../HoverCard/styles/styles.module.scss';
import CardBottomButtons from '../CardBottomButtons';
import CardImage from '../CardImage';
import CardLink from '../CardLink';
import CompletedBadge from '../../shared/badges/CompletedBadge/CompletedBadge';
import TrailCardContent from '../TrailCardContent';

type TrailHoverCardProps = {
  allowClickingCompletedBadge?: boolean;
  bottomButtonsConfig?: ButtonConfig[];
  context: Context;
  handleCardClick: () => void;
  handleCompletedBadgeClick?: () => void;
  handleFavoriteClick: () => void;
  handleLocationClick?: () => void;
  handleMouseEnter?: () => void;
  handleMouseLeave?: () => void;
  isCompleted?: boolean;
  isFavorite?: boolean;
  isVerified?: boolean;
  trail: Trail;
  trailUrl?: string;
};

export default function TrailHoverCard({
  allowClickingCompletedBadge,
  bottomButtonsConfig,
  context,
  handleCardClick,
  handleCompletedBadgeClick,
  handleFavoriteClick,
  handleLocationClick,
  handleMouseEnter,
  handleMouseLeave,
  isCompleted,
  isFavorite,
  isVerified,
  trail,
  trailUrl
}: TrailHoverCardProps) {
  const newMiniCards = useFeatures().includes('new_mini_cards');
  const languageRegionCode = useLanguageRegionCode();
  const minutes = useActivityDuration(trail);
  const footerCTAs = useAdminFooterCTAs(bottomButtonsConfig);

  if (newMiniCards) {
    // TODO: TD-350 Remove CustomProvider
    return (
      <CustomProvider>
        <NewTrailCard
          languageRegionCode={languageRegionCode}
          onCardClick={handleCardClick}
          onCardMouseEnter={handleMouseEnter}
          onCardMouseLeave={handleMouseLeave}
          onFavoriteClick={handleFavoriteClick}
          isFavorite={isFavorite}
          variant="small"
          trail={getTrailForTrailCard(trail, minutes, languageRegionCode)}
          useMetric={context?.displayMetric}
          footerCTAs={footerCTAs && footerCTAs.length ? footerCTAs : undefined}
          isCompleted={isCompleted}
          isVerified={isVerified}
          onCompletionBadgeClick={allowClickingCompletedBadge ? handleCompletedBadgeClick : undefined}
          url={trailUrl}
          blockNavigation={!!handleCardClick} // Navigation is handled by the click handler
          openInNewTab
        />
      </CustomProvider>
    );
  }

  if (!trailUrl) {
    trailUrl = getFormattedTrailUrls(trail, context?.languageRegionCode).localizedUrl;
  }
  return (
    <CustomProvider>
      <div className={styles.container} onMouseEnter={wrapEventHandler(handleMouseEnter)} onMouseLeave={wrapEventHandler(handleMouseLeave)}>
        {/* statically positioned */}
        <CardLink target="_blank" title={getLocalizedName(trail, context?.languageRegionCode)} url={trailUrl} handleClick={handleCardClick} />
        <CardImage className={styles.image} url={getTrailProfilePhotoUrl(trail, 'medium')} title={trail.name} lazyload={false} />
        <div className={styles.contentContainer}>
          <TrailCardContent
            className={styles.content}
            handleLocationClick={handleLocationClick}
            namePaddingRight={24}
            trail={trail}
            descriptive={false}
            displayRank={undefined}
            url={undefined}
          />
          <CardBottomButtons className={styles.bottomButtons} config={bottomButtonsConfig} />
        </div>
        {/* absolute positioned */}
        <CompletedBadge
          shortened
          className={styles.badge}
          isCompleted={isCompleted}
          isVerified={isVerified}
          handleClick={allowClickingCompletedBadge ? handleCompletedBadgeClick : null}
        />
        <BookmarkButton className={styles.bookmarkButton} onWhiteBackground onClick={wrapEventHandler(handleFavoriteClick)} isSelected={isFavorite} />
      </div>
    </CustomProvider>
  );
}
